import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios, { AxiosError, AxiosResponse } from "axios";
import { RootState } from "./store";
import { axiosApi } from "@/helpers";
import { ENDPOINTS_LANG, URL_LANG } from "../constants/api_endpoints";
import { IChannel, ILangState, LangType } from "../types/langTypes";
import { IResponse, RequestStatus, ResponseStatus } from "../types/statusTypes";

const initialState: ILangState = {
	translateList: {
		data: [],
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	translate: null,
	language: "",
	status: RequestStatus.IDLE,
	wallpaper: {
		status: RequestStatus.IDLE,
		dataUrl: null,
	},
	channelList: {
		status: RequestStatus.IDLE,
		data: [],
		dictionary: {},
		error: ResponseStatus.SUCCESS,
		message: "",
	},
};

// получение списка переводов
export const getTranslateList = createAsyncThunk("lang/list", async (_, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<string[] | IResponse> = await axios.get(`${URL_LANG}/${ENDPOINTS_LANG.LIST}`);
		return response.data;
	} catch (error) {
		if (error) {
			// возвращаем данные ошибки, пришедшие с бэка
			return rejectWithValue(error);
		}
	}
});

// получение словаря для перевода текста
export const getTranslate = createAsyncThunk("lang/translate", async ({ language }: { language?: string }, { signal, rejectWithValue }) => {
	try {
		const response: AxiosResponse<LangType> = await axios.get(`${URL_LANG}/`, {
			signal,
			params: {
				language,
			},
		});
		return response.data;
	} catch (error) {
		if (error) {
			// возвращаем данные ошибки, пришедшие с бэка
			return rejectWithValue(error);
		}
	}
});

// получение случайных обоев
export const getRandomWallpaper = createAsyncThunk("lang/wallpaper", async (): Promise<Blob> => {
	const response: AxiosResponse<Blob> = await axios.get(`${URL_LANG}/${ENDPOINTS_LANG.WALLPAPER}`, {
		responseType: "blob",
		// headers: {
		// 'Cache-Control': 'no-cache, no-store, must-revalidate',
		// 'Cache-Control': 'public, max-age=10',
		// },
	});
	return response.data;
});

// получение списка каналов
export const getChannelList = createAsyncThunk("lang/channels", async (_, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IChannel[] | IResponse> = await axiosApi.get(`${URL_LANG}/${ENDPOINTS_LANG.CHANNELS}`);
		return response.data;
	} catch (error) {
		if (error) {
			// возвращаем данные ошибки, пришедшие с бэка
			return rejectWithValue(error);
		}
	}
});

const langSlice = createSlice({
	name: "lang",
	initialState,
	reducers: {
		// очистка списка каналов
		clearChannelList: (state) => {
			state.channelList = initialState.channelList;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(getTranslateList.pending, (state) => {
				state.translateList.status = RequestStatus.LOADING;
			})
			.addCase(getTranslateList.fulfilled, (state, action) => {
				if (action.payload) {
					state.translateList.status = RequestStatus.IDLE;
					if (Array.isArray(action.payload)) state.translateList.data = action.payload;
				} else {
					state.translateList.status = RequestStatus.FAILED;
				}
			})
			.addCase(getTranslateList.rejected, (state, action: PayloadAction<unknown>) => {
				state.translateList.status = RequestStatus.FAILED;
				if (action.payload instanceof AxiosError && action.payload.response?.data.error && action.payload.response?.data.message) {
					state.translateList.error = action.payload.response?.data.error;
					state.translateList.message = action.payload.response?.data.message;
				}
			})
			.addCase(getTranslate.pending, (state) => {
				state.status = RequestStatus.LOADING;
			})
			.addCase(getTranslate.fulfilled, (state, action) => {
				state.status = RequestStatus.IDLE;
				if (action.payload) {
					state.translate = action.payload;
					state.language = action.meta.arg.language || "";
				}
			})
			.addCase(getTranslate.rejected, (state, action: PayloadAction<unknown>) => {
				if (action.payload instanceof AxiosError) {
					state.status = RequestStatus.FAILED;
				}
			})
			.addCase(getRandomWallpaper.pending, (state) => {
				state.wallpaper.status = RequestStatus.LOADING;
			})
			.addCase(getRandomWallpaper.fulfilled, (state, action) => {
				if (action.payload instanceof Blob && action.payload.type.includes("image")) {
					state.wallpaper.status = RequestStatus.IDLE;
					const url = window.URL.createObjectURL(action.payload);
					state.wallpaper.dataUrl = url;
				} else state.wallpaper.status = RequestStatus.FAILED;
			})
			.addCase(getRandomWallpaper.rejected, (state) => {
				state.wallpaper.status = RequestStatus.FAILED;
			})
			.addCase(getChannelList.pending, (state) => {
				state.channelList.status = RequestStatus.LOADING;
			})
			.addCase(getChannelList.fulfilled, (state, action) => {
				if (Array.isArray(action.payload)) {
					state.channelList.status = RequestStatus.IDLE;
					state.channelList.data = action.payload.sort((a, b) => {
						if (a.name > b.name) return 1;
						else if (a.name < b.name) return -1;
						else return 0;
					});
					const dictionary: Record<string, string> = {}; // создаем словарь id-name
					action.payload.forEach((channelElem) => (dictionary[channelElem.id] = channelElem.name));
					state.channelList.dictionary = dictionary;
				} else state.channelList.status = RequestStatus.FAILED;
			})
			.addCase(getChannelList.rejected, (state, action: PayloadAction<unknown>) => {
				state.channelList.status = RequestStatus.FAILED;
				if (action.payload instanceof AxiosError) {
					state.channelList.error = action.payload.response?.data.error;
					state.channelList.message = action.payload.response?.data.message;
				}
			});
	},
});

export const { clearChannelList } = langSlice.actions;

export const selectTranslateList = (state: RootState) => state.lang.translateList;
export const selectTranslate = (state: RootState) => state.lang.translate;
export const selectTranslateLanguage = (state: RootState) => state.lang.language;
export const selectTranslateStatus = (state: RootState) => state.lang.status;

export const selectWallpaper = (state: RootState) => state.lang.wallpaper;
export const selectChannelList = (state: RootState) => state.lang.channelList;

export default langSlice.reducer;
